import { clsx } from 'clsx';
import { useId, type ReactNode } from 'react';

/* -------------------------------------------------------------------------
   SectionCard — the single container primitive.

   Every panel in the console is this component. One container means one set of
   border, radius, padding and header decisions, which is most of what separates
   a console that feels designed from one that feels assembled (§32).
   ------------------------------------------------------------------------- */

export interface SectionCardProps {
  title?: string;
  description?: string;
  /** Right-aligned controls in the card header. */
  actions?: ReactNode;
  /** Removes body padding — for tables that draw their own. */
  flush?: boolean;
  children: ReactNode;
  className?: string;
}

export function SectionCard({
  title,
  description,
  actions,
  flush = false,
  children,
  className,
}: SectionCardProps) {
  const headingId = useId();

  return (
    /*
      A titled card is a landmark, and its title is the label.

      Without `aria-labelledby` this is an anonymous `<section>` — which conveys
      nothing, so a screen-reader user navigating by region finds a list of
      unnamed boxes and has to read into each one to learn what it holds. The
      heading is already on screen; wiring it up costs nothing and turns every
      panel in the console into somewhere you can jump to by name.

      Untitled cards stay plain `<section>` elements: a region with no
      accessible name is noise in the landmark list, not structure.
    */
    <section
      {...(title ? { 'aria-labelledby': headingId } : {})}
      className={clsx(
        'overflow-hidden rounded-[var(--vc-radius-lg)] border border-[var(--vc-border)]',
        'bg-[var(--vc-surface)] shadow-[var(--vc-shadow-sm)]',
        className,
      )}
    >
      {title ? (
        <header
          className={clsx(
            'flex items-start justify-between gap-4 border-b border-[var(--vc-border-subtle)]',
            'px-4 py-3',
          )}
        >
          <div className="min-w-0">
            <h2 id={headingId} className="text-[13px] font-semibold text-[var(--vc-text)]">
              {title}
            </h2>
            {description ? (
              <p className="mt-0.5 text-[12px] text-[var(--vc-text-muted)]">{description}</p>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}

      <div className={flush ? '' : 'p-4'}>{children}</div>
    </section>
  );
}

/* -------------------------------------------------------------------------
   MetricCard
   ------------------------------------------------------------------------- */

export interface MetricCardProps {
  label: string;
  /** `null` renders an em dash — the honest representation of "no data yet" (§30). */
  value: string | number | null;
  unit?: string;
  hint?: string;
  icon?: ReactNode;
  className?: string;
}

export function MetricCard({ label, value, unit, hint, icon, className }: MetricCardProps) {
  const hasValue = value !== null && value !== undefined;
  const labelId = useId();

  return (
    /*
      A named group, not a bare div.

      The label and the number used to be two unrelated fragments, so a screen
      reader announced "3" with nothing attached to it — the value is useless
      without the thing it counts. Naming the group ties them together and makes
      the card addressable in tests by the same name a person hears.
    */
    <div
      role="group"
      aria-labelledby={labelId}
      className={clsx(
        'rounded-[var(--vc-radius-lg)] border border-[var(--vc-border)] bg-[var(--vc-surface)]',
        'p-4 shadow-[var(--vc-shadow-sm)]',
        className,
      )}
    >
      <div className="flex items-center gap-2">
        {icon ? (
          <span aria-hidden="true" className="text-[var(--vc-text-subtle)] [&>svg]:size-3.5">
            {icon}
          </span>
        ) : null}
        <span id={labelId} className="text-[12px] font-medium text-[var(--vc-text-muted)]">
          {label}
        </span>
      </div>

      <div className="mt-2.5 flex items-baseline gap-1.5">
        <span
          className={clsx(
            'text-[26px] leading-none font-semibold tracking-[-0.02em]',
            hasValue ? 'text-[var(--vc-text)]' : 'text-[var(--vc-text-subtle)]',
          )}
        >
          {hasValue ? value : '—'}
        </span>
        {unit && hasValue ? (
          <span className="text-[12px] text-[var(--vc-text-subtle)]">{unit}</span>
        ) : null}
      </div>

      {hint ? <p className="mt-2 text-[11px] text-[var(--vc-text-subtle)]">{hint}</p> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------
   DefinitionList — the label/value pattern used by every detail panel.
   ------------------------------------------------------------------------- */

export interface DefinitionItem {
  label: string;
  value: ReactNode;
}

export function DefinitionList({
  items,
  className,
}: {
  items: readonly DefinitionItem[];
  className?: string;
}) {
  return (
    <dl className={clsx('grid gap-x-6 gap-y-3 sm:grid-cols-2', className)}>
      {items.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-[11px] font-medium tracking-[0.02em] text-[var(--vc-text-subtle)] uppercase">
            {item.label}
          </dt>
          <dd className="mt-1 truncate text-[13px] text-[var(--vc-text)]">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* -------------------------------------------------------------------------
   CodeBlock
   ------------------------------------------------------------------------- */

export function CodeBlock({
  code,
  language,
  className,
}: {
  code: string;
  language?: string;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'overflow-hidden rounded-[var(--vc-radius-md)] border border-[var(--vc-code-border)]',
        'bg-[var(--vc-code-bg)]',
        className,
      )}
    >
      {language ? (
        <div
          className={clsx(
            'flex items-center border-b border-[var(--vc-code-border)] px-3 py-1.5',
            'text-[11px] font-medium tracking-[0.03em] text-[var(--vc-graphite-400)] uppercase',
          )}
        >
          {language}
        </div>
      ) : null}
      <pre className="overflow-x-auto p-3">
        <code className="font-[family-name:var(--vc-font-mono)] text-[12px] leading-relaxed text-[var(--vc-code-text)]">
          {code}
        </code>
      </pre>
    </div>
  );
}
